import axios from '@/plugins/axios';

export const userNotification = {
  namespaced: true,

  state: {
    showModalUserNotification: false,
    notificationContent: null,
    dontShowAgainUserNotification: false,
    currentNotificationId: null,
    unseenNotifications: [],
    viewedNotifications: [],
    realtimeNotifications: [],
    currentUserId: null,
    yearlyNotifications: [],
    selectedNotificationId: null,
  },

  mutations: {
    SET_SHOW_MODAL(state, value) {
      state.showModalUserNotification = value;
    },
    SET_NOTIFICATION_CONTENT(state, content) {
      state.notificationContent = content;
    },
    SET_DONT_SHOW_AGAIN(state, value) {
      state.dontShowAgainUserNotification = value;
    },
    SET_UNSEEN_NOTIFICATIONS(state, notifications) {
      state.unseenNotifications = notifications;
    },
    SET_CURRENT_NOTIFICATION_ID(state, id) {
      state.currentNotificationId = id;
    },
    SET_VIEWED_NOTIFICATIONS(state, viewedIds) {
      state.viewedNotifications = viewedIds;
    },
    ADD_VIEWED_NOTIFICATION(state, notificationId) {
      if (!state.viewedNotifications.includes(notificationId)) {
        state.viewedNotifications.push(notificationId);
      }
    },
    ADD_REALTIME_NOTIFICATION(state, notification) {
      const exists = state.realtimeNotifications.find(
        (n) => n.notification_id === notification.notification_id
      );
      if (!exists) {
        state.realtimeNotifications.unshift({
          id: Date.now() + Math.random(),
          published_at: notification.published_at || notification.timestamp,
          created_at: notification.timestamp,
          ...notification,
        });

        if (state.realtimeNotifications.length > 20) {
          state.realtimeNotifications = state.realtimeNotifications.slice(
            0,
            20
          );
        }
      }
    },
    SET_YEARLY_NOTIFICATIONS(state, notifications) {
      state.yearlyNotifications = notifications;
    },
    SET_SELECTED_NOTIFICATION_ID(state, id) {
      state.selectedNotificationId = id;
    },
    SET_CURRENT_USER_ID(state, userId) {
      state.currentUserId = userId;
    },
  },

  actions: {
    async checkUserNotification({ commit, rootGetters }) {
      const userId = rootGetters['auth/user'].id;
      let userRole = rootGetters['auth/user'].roles[0]?.name || '';
      const userChannel = rootGetters['auth/user'].VTWEG;

      if (!userChannel || userRole === 'NotificationAdmin') return;

      const viewedResponse = await axios.get(
        `/user/createviewnotification?user_id=${userId}`
      );
      const viewedNotifications = viewedResponse.data.map(
        (v) => v.id_notification
      );
      commit('SET_VIEWED_NOTIFICATIONS', viewedNotifications);

      const response = await axios.get(`/user/createnotification`);
      const updates = response.data;

      if (updates && updates.length > 0) {
        let relevantUpdates = updates.filter((update) => {
          let adjustedRole = update.role;
          if (
            adjustedRole.includes('Customer') &&
            update.channel.includes('PR')
          ) {
            adjustedRole = `${adjustedRole},CustomerPR`;
          }
          const userHasRole =
            adjustedRole.includes(userRole) || adjustedRole.includes('Todos');
          const userHasChannel = update.channel.includes(userChannel);
          const isActive = update.state === 1;
          return (
            userHasRole &&
            userHasChannel &&
            isActive &&
            !viewedNotifications.includes(update.id)
          );
        });

        if (relevantUpdates.length > 0) {
          relevantUpdates.sort(
            (a, b) => new Date(b.published_at) - new Date(a.published_at)
          );

          const notificationContent = relevantUpdates
            .map((n) => {
              let versionText =
                n.version && n.version !== '0'
                  ? `<h6><b>Hemos actualizado tu aplicación ${n.version}</b></h6>`
                  : '';
              return `
                  <div style="text-align: center;">
                    ${versionText}
                    <p class="h6">Nos complace trabajar para que tengas la mejor experiencia en IEL</p>
                  </div>
                  <div>${n.content}</div>
                `;
            })
            .join('\n\n');

          commit('SET_NOTIFICATION_CONTENT', notificationContent);
          commit('SET_SHOW_MODAL', true);
          commit('SET_UNSEEN_NOTIFICATIONS', relevantUpdates);
        }
      }
    },

    async markNotificationsAsViewed({ state, rootGetters, commit }) {
      const userId = rootGetters['auth/user'].id;
      const notificationIds = state.unseenNotifications.map((n) => n.id);

      try {
        await Promise.all(
          notificationIds.map((id) =>
            axios.post('/createviewnotification', {
              id_user: userId,
              id_notification: id,
            })
          )
        );

        notificationIds.forEach((id) => {
          commit('ADD_VIEWED_NOTIFICATION', id);
        });

        commit('SET_UNSEEN_NOTIFICATIONS', []);

        return { success: true, markedIds: notificationIds };
      } catch (error) {
        notificationIds.forEach((id) => {
          commit('ADD_VIEWED_NOTIFICATION', id);
        });

        return { success: false, error };
      }
    },

    setCurrentUser({ commit }, userId) {
      commit('SET_CURRENT_USER_ID', userId);
    },

    async fetchNotificationsByRole({ commit, rootGetters }, role = null) {
      const userId = rootGetters['auth/user'].id;
      let userRole = role || rootGetters['auth/user'].roles[0]?.name || '';
      const userChannel = rootGetters['auth/user'].VTWEG;

      if (!userChannel || userRole === 'NotificationAdmin') return;

      const viewedResponse = await axios.get(
        `/user/createviewnotification?user_id=${userId}`
      );
      const viewedNotifications = viewedResponse.data.map(
        (v) => v.id_notification
      );
      commit('SET_VIEWED_NOTIFICATIONS', viewedNotifications);

      const response = await axios.get(
        `/notifications/current-year?role=${userRole}`
      );
      const updates = response.data.notifications || [];

      if (updates && updates.length > 0) {
        let relevantUpdates = updates.filter((update) => {
          let adjustedRole = update.role;
          if (
            adjustedRole.includes('Customer') &&
            update.channel.includes('PR')
          ) {
            adjustedRole = `${adjustedRole},CustomerPR`;
          }
          const userHasRole =
            adjustedRole.includes(userRole) || adjustedRole.includes('Todos');
          const userHasChannel = update.channel.includes(userChannel);
          const isActive = update.state === 1;
          return userHasRole && userHasChannel && isActive;
        });

        commit('SET_YEARLY_NOTIFICATIONS', relevantUpdates);

        const unseenUpdates = relevantUpdates.filter(
          (update) => !viewedNotifications.includes(update.id)
        );

        if (unseenUpdates.length > 0) {
          unseenUpdates.sort(
            (a, b) => new Date(b.published_at) - new Date(a.published_at)
          );

          const notificationContent = unseenUpdates
            .map((n) => {
              let versionText =
                n.version && n.version !== '0'
                  ? `<h6><b>Hemos actualizado tu aplicación ${n.version}</b></h6>`
                  : '';
              return `
                  <div style="text-align: center;">
                    ${versionText}
                    <p class="h6">Nos complace trabajar para que tengas la mejor experiencia en IEL</p>
                  </div>
                  <div>${n.content}</div>
                `;
            })
            .join('\n\n');

          commit('SET_NOTIFICATION_CONTENT', notificationContent);
          commit('SET_SHOW_MODAL', true);
          commit('SET_UNSEEN_NOTIFICATIONS', unseenUpdates);
        }
      }
    },

    selectNotification({ commit }, notificationId) {
      commit('SET_SELECTED_NOTIFICATION_ID', notificationId);
    },
    clearSelectedNotification({ commit }) {
      commit('SET_SELECTED_NOTIFICATION_ID', null);
    },
  },

  getters: {
    showModal: (state) => state.showModalUserNotification,
    notificationContent: (state) => state.notificationContent,
    dontShowAgain: (state) => state.dontShowAgainUserNotification,
    hasNotifications: (state) => state.unseenNotifications.length > 0,
    unreadCount: (state) =>
      state.unseenNotifications.length +
      state.realtimeNotifications.filter(
        (n) => !state.viewedNotifications.includes(n.notification_id)
      ).length,
    notifications: (state) => {
      const allNotifications = [
        ...state.unseenNotifications,
        ...state.realtimeNotifications,
      ];
      return allNotifications.filter(
        (n) => !state.viewedNotifications.includes(n.notification_id || n.id)
      );
    },
    notificationsByYear: (state) => {
      const allNotifications = [
        ...state.yearlyNotifications,
        ...state.unseenNotifications,
        ...state.realtimeNotifications,
      ].map((n) => ({
        ...n,
        read: state.viewedNotifications.includes(n.notification_id || n.id),
      }));
      const unique = {};
      allNotifications.forEach((n) => {
        unique[n.notification_id || n.id] = n;
      });
      return Object.values(unique).sort(
        (a, b) =>
          new Date(b.published_at || b.timestamp) -
          new Date(a.published_at || a.timestamp)
      );
    },
    selectedNotificationDetails: (state) => {
      if (!state.selectedNotificationId) return null;
      const allNotifications = [
        ...state.yearlyNotifications,
        ...state.unseenNotifications,
        ...state.realtimeNotifications,
      ];
      const selectedNotification = allNotifications.find((notification) => {
        const id = notification.notification_id || notification.id;
        return id === state.selectedNotificationId;
      });
      if (!selectedNotification) return null;
      return {
        id: selectedNotification.notification_id || selectedNotification.id,
        content: selectedNotification.content,
        version: selectedNotification.version,
        publishedAt:
          selectedNotification.published_at || selectedNotification.timestamp,
        isRead: state.viewedNotifications.includes(
          selectedNotification.notification_id || selectedNotification.id
        ),
        role: selectedNotification.role,
        channel: selectedNotification.channel,
        state: selectedNotification.state,
        rawNotification: selectedNotification,
      };
    },
    notificationPreviewContent: (state, getters) => {
      const selectedDetails = getters.selectedNotificationDetails;
      if (!selectedDetails) return '';
      let versionText =
        selectedDetails.version && selectedDetails.version !== '0'
          ? `<h6><b>Hemos actualizado tu aplicación ${selectedDetails.version}</b></h6>`
          : '';
      return `
        <div style="text-align: center;">
          ${versionText}
          <p class="h6">Nos complace trabajar para que tengas la mejor experiencia en IEL</p>
        </div>
        <div>${selectedDetails.content}</div>
      `;
    },
  },
};

export default userNotification;
